import React, { useState, useRef } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const TIERS = [
  { name: 'Starter', min: 1, max: 99, price: 24.99, description: 'For individual teams and small programs' },
  { name: 'Program', min: 100, max: 999, price: 16.99, description: 'For full athletic departments', popular: true },
  { name: 'District', min: 1000, max: null, price: 5.99, description: 'District or conference-wide licensing' },
];

const LESSONS = [
  { num: 1, title: 'The Basics', description: 'Define NIL and understand how it applies to student-athletes in the context of changing NCAA regulations.' },
  { num: 2, title: 'Building a Personal Brand', description: 'Identify personal brand elements and develop a basic brand statement to guide NIL-related decisions.' },
  { num: 3, title: 'Navigating Legal & Financial Aspects', description: 'Evaluate common NIL contract terms and understand legal and ethical considerations before signing agreements.' },
  { num: 4, title: 'Leveraging NIL for Community Impact', description: 'Engage with community organizations through NIL activities that reflect positive personal and social impact.' },
  { num: 5, title: 'Preparing for NIL Opportunities', description: 'Create a social media strategy aligned with personal values, NCAA policies, and long-term career goals.' },
  { num: 6, title: 'Protecting Athletes', description: 'Explain basic financial literacy concepts such as budgeting, saving, and tax responsibilities relevant to NIL earnings.' },
  { num: 7, title: 'Personal Playbook', description: 'Complete a self-assessment and identify next steps to prepare for real-world NIL opportunities.' },
];

const OUTCOMES = [
  'Define Name, Image, and Likeness (NIL) and explain how it applies to student-athletes in the context of changing NCAA regulations',
  'Identify personal brand elements and develop a basic brand statement to guide NIL-related decisions',
  'Evaluate common NIL contract terms and describe the importance of legal and ethical considerations before signing agreements',
  'Create a social media strategy that aligns with personal values, NCAA policies, and long-term career goals',
  'Explain basic financial literacy concepts such as budgeting, saving, and tax responsibilities relevant to NIL earnings',
  'Outline steps to engage with community organizations through NIL activities that reflect positive personal and social impact',
  'Reflect on their NIL readiness by completing a self-assessment and identifying next steps to prepare for real-world NIL opportunities',
];

function getTier(seats) {
  return TIERS.find(t => seats >= t.min && (t.max === null || seats <= t.max)) || TIERS[0];
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function parseEmailsFromText(text) {
  return text
    .split(/[\n,;]+/)
    .map(e => e.trim().toLowerCase())
    .filter(e => e.length > 0);
}

const NilU = () => {
  const [step, setStep] = useState(0);
  const [seats, setSeats] = useState(100);
  const [form, setForm] = useState({ name: '', email: '', organization: '' });
  const [emails, setEmails] = useState([]);
  const [emailText, setEmailText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVideo, setShowVideo] = useState(false);
  const fileRef = useRef(null);
  const pricingRef = useRef(null);
  const formRef = useRef(null);

  const tier = getTier(seats);
  const total = (tier.price * seats).toFixed(2);
  const validEmails = emails.filter(e => validateEmail(e));
  const invalidEmails = emails.filter(e => !validateEmail(e));

  const handleSeatsChange = (value) => {
    const num = Math.max(1, parseInt(value) || 1);
    setSeats(num);
  };

  const handleEmailTextChange = (text) => {
    setEmailText(text);
    const parsed = parseEmailsFromText(text);
    setEmails(parsed);
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n');
      const extracted = [];

      const header = lines[0]?.toLowerCase();
      let emailColIndex = -1;

      if (header) {
        const cols = header.split(',');
        emailColIndex = cols.findIndex(c => c.trim().includes('email'));
      }

      const startLine = emailColIndex >= 0 ? 1 : 0;

      for (let i = startLine; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        if (emailColIndex >= 0) {
          const cols = line.split(',');
          const email = cols[emailColIndex]?.trim().toLowerCase();
          if (email && validateEmail(email)) extracted.push(email);
        } else {
          const cells = line.split(',');
          for (const cell of cells) {
            const email = cell.trim().toLowerCase();
            if (validateEmail(email)) extracted.push(email);
          }
        }
      }

      const allEmails = [...new Set([...emails, ...extracted])];
      setEmails(allEmails);
      setEmailText(allEmails.join('\n'));
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleStartPurchase = () => {
    setStep(1);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleCheckout = async () => {
    setLoading(true);
    setError('');

    try {
      const orderRef = await addDoc(collection(db, 'nil-u-orders'), {
        purchaserName: form.name,
        purchaserEmail: form.email,
        organization: form.organization,
        seats,
        pricePerSeat: tier.price,
        total: parseFloat(total),
        tierName: tier.name,
        studentEmails: validEmails,
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: orderRef.id,
          seats,
          pricePerSeat: tier.price,
          total: parseFloat(total),
          customerEmail: form.email,
          customerName: form.name,
          organization: form.organization,
          tierName: tier.name,
        }),
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const canProceedToEmails = form.name && form.email && form.organization && validateEmail(form.email);
  const canProceedToReview = validEmails.length > 0 && invalidEmails.length === 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-black py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-spi-400 text-sm font-semibold tracking-widest uppercase mb-4">
            Sonom Performance Institute
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-6 tracking-tight">
            NIL U
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto mb-10">
            A student-centered guide to mastering Name, Image, and Likeness through purpose, strategy, and leadership.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToPricing}
              className="px-8 py-3.5 bg-spi-600 text-white hover:bg-spi-700 transition-colors rounded-lg font-medium text-lg"
            >
              Get Started
            </button>
            <button
              onClick={() => setShowVideo(true)}
              className="px-8 py-3.5 bg-white text-gray-900 hover:bg-gray-100 transition-colors rounded-lg font-medium text-lg inline-flex items-center justify-center gap-2"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
              Watch Overview
            </button>
            <a
              href="https://share.articulate.com/MQuxgXiWzndFXPylj4LpF"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3.5 border-2 border-gray-600 text-gray-300 hover:border-white hover:text-white transition-colors rounded-lg font-medium text-lg"
            >
              Preview Course Free
            </a>
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section id="outcomes" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-light text-gray-900 mb-4">
              What Students Will Learn
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              By completing NIL U, learners will be able to:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {OUTCOMES.map((outcome, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-spi-100 text-spi-700 flex items-center justify-center text-sm font-semibold">
                  {i + 1}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{outcome}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Modules */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-light text-gray-900 mb-4">
              Course Modules
            </h2>
            <p className="text-gray-500 text-lg">
              7 comprehensive lessons designed for student-athletes
            </p>
          </div>

          <div className="space-y-4">
            {LESSONS.map((lesson) => (
              <div key={lesson.num} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-black text-white flex items-center justify-center font-light text-lg">
                    {lesson.num}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{lesson.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{lesson.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section ref={pricingRef} className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-light text-gray-900 mb-4">
              Volume Pricing
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Purchase seats for your team, department, or entire district. The more students you enroll, the more you save.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {TIERS.map((t) => (
              <div
                key={t.name}
                className={`relative rounded-2xl border-2 p-8 text-center transition-all ${
                  t.popular
                    ? 'border-spi-600 bg-spi-50 shadow-lg scale-[1.02]'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {t.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="bg-spi-600 text-white text-xs font-semibold px-4 py-1.5 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t.name}</h3>
                <p className="text-gray-500 text-sm mb-6">{t.description}</p>
                <div className="mb-2">
                  <span className="text-4xl font-light text-gray-900">${t.price}</span>
                  <span className="text-gray-500 text-sm"> / seat</span>
                </div>
                <p className="text-gray-400 text-xs mb-8">
                  {t.max ? `${t.min}–${t.max} students` : `${t.min}+ students`}
                </p>
                <button
                  onClick={() => { setSeats(t.min); handleStartPurchase(); }}
                  className={`w-full py-3 rounded-lg font-medium transition-colors ${
                    t.popular
                      ? 'bg-spi-600 text-white hover:bg-spi-700'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Select Plan
                </button>
              </div>
            ))}
          </div>

          {/* Quick Calculator */}
          <div className="max-w-lg mx-auto bg-gray-50 rounded-2xl p-8 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Price Calculator</h3>
            <div className="flex items-center gap-4 mb-4">
              <label className="text-gray-600 text-sm font-medium whitespace-nowrap">Number of seats:</label>
              <input
                type="number"
                min="1"
                value={seats}
                onChange={(e) => handleSeatsChange(e.target.value)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-spi-500 focus:border-spi-500 outline-none"
              />
            </div>
            <div className="flex justify-between items-center py-3 border-t border-gray-200">
              <span className="text-gray-500 text-sm">Tier</span>
              <span className="text-gray-900 font-medium">{tier.name} (${tier.price}/seat)</span>
            </div>
            <div className="flex justify-between items-center py-3 border-t border-gray-200">
              <span className="text-gray-500 text-sm">Total</span>
              <span className="text-2xl font-light text-gray-900">${total}</span>
            </div>
            <button
              onClick={handleStartPurchase}
              className="w-full mt-4 py-3 bg-spi-600 text-white hover:bg-spi-700 transition-colors rounded-lg font-medium"
            >
              Purchase {seats} Seat{seats !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </section>

      {/* Purchase Form */}
      {step > 0 && (
        <section ref={formRef} className="py-20 bg-gray-50 border-t border-gray-200">
          <div className="max-w-2xl mx-auto px-6 lg:px-8">
            {/* Step Indicator */}
            <div className="flex items-center justify-center mb-12">
              {['Your Info', 'Student Emails', 'Review'].map((label, i) => (
                <React.Fragment key={label}>
                  {i > 0 && (
                    <div className={`w-12 sm:w-20 h-0.5 ${step > i ? 'bg-spi-600' : 'bg-gray-300'}`} />
                  )}
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                        step > i + 1
                          ? 'bg-spi-600 text-white'
                          : step === i + 1
                          ? 'bg-spi-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {step > i + 1 ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className="text-xs text-gray-500 mt-1.5 hidden sm:block">{label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Organization Info */}
            {step === 1 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="text-2xl font-light text-gray-900 mb-2">Purchaser Information</h3>
                <p className="text-gray-500 text-sm mb-8">Tell us about you and your organization.</p>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Coach John Smith"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-spi-500 focus:border-spi-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="coach@school.edu"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-spi-500 focus:border-spi-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">School / Organization</label>
                    <input
                      type="text"
                      value={form.organization}
                      onChange={(e) => setForm({ ...form, organization: e.target.value })}
                      placeholder="Springfield High School"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-spi-500 focus:border-spi-500 outline-none"
                    />
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => setStep(0)}
                    className="px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors font-medium"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => canProceedToEmails && setStep(2)}
                    disabled={!canProceedToEmails}
                    className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                      canProceedToEmails
                        ? 'bg-spi-600 text-white hover:bg-spi-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Student Emails */}
            {step === 2 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="text-2xl font-light text-gray-900 mb-2">Student Email Addresses</h3>
                <p className="text-gray-500 text-sm mb-8">
                  Enter the email addresses for your {seats} seat{seats !== 1 ? 's' : ''}.
                  You can paste emails or upload a CSV file.
                </p>

                {/* CSV Upload */}
                <div className="mb-6">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleCSVUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center px-5 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-spi-400 hover:text-spi-600 transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload CSV or TXT
                  </button>
                  <span className="text-gray-400 text-xs ml-3">One email per row, or a column labeled "email"</span>
                </div>

                {/* Email Textarea */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Student Emails
                    <span className="text-gray-400 font-normal"> (one per line, or separated by commas)</span>
                  </label>
                  <textarea
                    value={emailText}
                    onChange={(e) => handleEmailTextChange(e.target.value)}
                    rows={10}
                    placeholder={"student1@school.edu\nstudent2@school.edu\nstudent3@school.edu"}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-spi-500 focus:border-spi-500 outline-none font-mono text-sm resize-y"
                  />
                </div>

                {/* Email Count & Validation */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-4">
                    <span className={`text-sm font-medium ${
                      validEmails.length === seats ? 'text-green-600' :
                      validEmails.length > seats ? 'text-amber-600' : 'text-gray-500'
                    }`}>
                      {validEmails.length} / {seats} email{seats !== 1 ? 's' : ''} entered
                    </span>
                    {validEmails.length > seats && (
                      <span className="text-amber-600 text-xs">More emails than seats</span>
                    )}
                  </div>
                  {emails.length > 0 && (
                    <button
                      onClick={() => { setEmails([]); setEmailText(''); }}
                      className="text-red-500 hover:text-red-600 text-xs font-medium"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {invalidEmails.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-700 text-sm font-medium mb-1">Invalid email addresses:</p>
                    <p className="text-red-600 text-xs font-mono">
                      {invalidEmails.slice(0, 5).join(', ')}
                      {invalidEmails.length > 5 && ` and ${invalidEmails.length - 5} more`}
                    </p>
                  </div>
                )}

                {validEmails.length < seats && validEmails.length > 0 && (
                  <p className="text-gray-400 text-xs mb-4">
                    You can provide the remaining {seats - validEmails.length} email{seats - validEmails.length !== 1 ? 's' : ''} later by contacting us.
                  </p>
                )}

                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors font-medium"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => canProceedToReview && setStep(3)}
                    disabled={!canProceedToReview}
                    className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                      canProceedToReview
                        ? 'bg-spi-600 text-white hover:bg-spi-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Review Order
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="text-2xl font-light text-gray-900 mb-6">Review Your Order</h3>

                <div className="space-y-4 mb-8">
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-500">Purchaser</span>
                    <span className="text-gray-900 font-medium">{form.name}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-500">Email</span>
                    <span className="text-gray-900">{form.email}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-500">Organization</span>
                    <span className="text-gray-900">{form.organization}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-500">Plan</span>
                    <span className="text-gray-900 font-medium">{tier.name}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-500">Seats</span>
                    <span className="text-gray-900">{seats}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-500">Price per Seat</span>
                    <span className="text-gray-900">${tier.price}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-500">Student Emails Provided</span>
                    <span className="text-gray-900">{validEmails.length}</span>
                  </div>
                  <div className="flex justify-between py-4">
                    <span className="text-gray-900 font-semibold text-lg">Total</span>
                    <span className="text-gray-900 font-semibold text-2xl">${total}</span>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(2)}
                    className="px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors font-medium"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleCheckout}
                    disabled={loading}
                    className={`px-10 py-3.5 rounded-lg font-medium text-lg transition-colors ${
                      loading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-spi-600 text-white hover:bg-spi-700'
                    }`}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      `Pay $${total}`
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      {step === 0 && (
        <section className="py-20 bg-black">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-light text-white mb-6">
              Ready to empower your athletes?
            </h2>
            <p className="text-gray-400 text-lg mb-10 leading-relaxed">
              Equip your student-athletes with the knowledge and tools they need to navigate NIL opportunities with confidence, integrity, and purpose.
            </p>
            <button
              onClick={scrollToPricing}
              className="px-10 py-4 bg-spi-600 text-white hover:bg-spi-700 transition-colors rounded-lg font-medium text-lg"
            >
              View Pricing
            </button>
          </div>
        </section>
      )}

      {/* Video Modal */}
      {showVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
          onClick={() => setShowVideo(false)}
        >
          <div
            className="relative w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <iframe
              src="https://www.youtube.com/embed/gO5D364G4Pg?autoplay=1"
              title="NIL U Overview"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full rounded-xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NilU;
